import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCloudUploadAlt,
  faCheck,
  faCheckCircle,
  faBold,
  faItalic,
  faLink,
  faListUl,
  faListOl,
  faCode,
  faFileAlt,
  faFileImage,
  faFileCode,
  faFilePdf,
  faUserTie,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { formatFileSize, getFileMeta, getStatusBadge } from './tagStatusHelper';

/**
 * Modal moderno para crear o editar una incidencia técnica.
 * Diseño fiel al mockup de diseño con soporte para:
 * - Asignación de responsable del sistema
 * - Barra de herramientas Markdown
 * - Dropzone de archivos adjuntos
 * - Pegado directo de imágenes desde el portapapeles (Ctrl + V)
 */
const TagCreateModal = ({
  isOpen,
  selectedTag = null,
  formName,
  setFormName,
  formDescription,
  setFormDescription,
  formStatus,
  setFormStatus,
  formResponsible,
  setFormResponsible,
  agentsList = [],
  isLoadingAgents = false,
  filesList = [],
  setFilesList,
  isSaving,
  onSave,
  onClose,
  isDark = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const lastPasteTimeRef = useRef(0);

  // Insertar formato Markdown en la posición del cursor de la descripción
  const handleInsertMarkdown = (prefix, suffix = '', defaultText = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formDescription.substring(start, end) || defaultText;
    const replacement = `${prefix}${selectedText}${suffix}`;

    const newText =
      formDescription.substring(0, start) +
      replacement +
      formDescription.substring(end);

    setFormDescription(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + selectedText.length
      );
    }, 0);
  };

  // Procesar archivos subidos (vía input, drag & drop o pegado de imágenes)
  const processFiles = useCallback(
    incomingFiles => {
      if (!incomingFiles || incomingFiles.length === 0) return;

      const newFiles = Array.from(incomingFiles);
      newFiles.forEach(file => {
        if (file.size > 20 * 1024 * 1024) {
          alert(`El archivo "${file.name}" supera el límite de 20 MB.`);
          return;
        }

        const reader = new FileReader();
        reader.onload = e => {
          const dataUrl = e.target?.result;
          if (!dataUrl) return;

          let cleanName = file.name;
          if (!cleanName || cleanName === 'image.png' || cleanName === 'blob') {
            const now = new Date();
            const timePart = `${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
            cleanName = `captura-incidencia-${timePart}.png`;
          }

          const preparedItem = {
            id: 'file_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
            fileName: cleanName,
            fileSize: file.size,
            dataUrl: dataUrl,
            fileObject: file,
            isPendingUpload: true
          };

          setFilesList(prev => {
            // Evitar duplicados si ya existe un archivo con mismo nombre y tamaño
            const isDuplicate = prev.some(
              item => item.fileName === cleanName && item.fileSize === file.size
            );
            if (isDuplicate) return prev;
            return [...prev, preparedItem];
          });
        };
        reader.readAsDataURL(file);
      });

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [setFilesList]
  );

  // Manejar pegado de imágenes desde el portapapeles (Ctrl + V)
  const handlePaste = useCallback(
    e => {
      const now = Date.now();
      if (now - lastPasteTimeRef.current < 400) return;

      const clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items || items.length === 0) return;

      const pastedImages = [];
      const seenKeys = new Set();

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            const key = `${blob.name || 'img'}_${blob.size}`;
            if (seenKeys.has(key)) continue;
            seenKeys.add(key);

            let fileName = blob.name;
            if (!fileName || fileName === 'image.png' || fileName === 'blob') {
              const dateObj = new Date();
              const timePart = `${dateObj.getHours().toString().padStart(2, '0')}${dateObj.getMinutes().toString().padStart(2, '0')}${dateObj.getSeconds().toString().padStart(2, '0')}`;
              fileName = `captura-incidencia-${timePart}.png`;
            }
            const file = new File([blob], fileName, { type: blob.type || 'image/png' });
            pastedImages.push(file);
          }
        }
      }

      if (pastedImages.length > 0) {
        lastPasteTimeRef.current = now;
        // Evitar que el navegador intente pegar datos binarios en el textarea
        e.preventDefault();
        processFiles(pastedImages);
      }
    },
    [processFiles]
  );

  // Escuchar pegado de imágenes mientras el modal esté abierto
  useEffect(() => {
    if (!isOpen) return;

    const onGlobalPaste = e => {
      handlePaste(e);
    };

    window.addEventListener('paste', onGlobalPaste);
    return () => window.removeEventListener('paste', onGlobalPaste);
  }, [isOpen, handlePaste]);

  if (!isOpen) return null;

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = fileId => {
    setFilesList(prev => prev.filter(f => f.id !== fileId));
  };

  // Calcular peso total de los archivos preparados
  const totalBytes = filesList.reduce((acc, f) => acc + (f.fileSize || 0), 0);

  // Icono para cada tipo de archivo
  const renderFileIcon = fileName => {
    const { type } = getFileMeta(fileName);
    switch (type) {
      case 'image':
        return <FontAwesomeIcon icon={faFileImage} style={{ color: '#6366f1' }} />;
      case 'code':
        return <FontAwesomeIcon icon={faFileCode} style={{ color: '#6366f1' }} />;
      case 'pdf':
        return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#ef4444' }} />;
      default:
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#0ea5e9' }} />;
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', zIndex: 1060 }}
      tabIndex="-1"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: '680px', width: '95%' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="modal-content border-0 shadow-2xl"
          style={{
            backgroundColor: isDark ? '#141e2c' : '#ffffff',
            borderRadius: '24px',
            overflow: 'hidden'
          }}
        >
          {/* Header del Modal */}
          <div
            className="p-4 pb-3 d-flex align-items-start justify-content-between border-bottom"
            style={{ borderColor: isDark ? '#223247' : '#f1f5f9' }}
          >
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5
                  className="fw-bold mb-0"
                  style={{
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '1.30rem',
                    letterSpacing: '-0.3px'
                  }}
                >
                  {selectedTag ? 'Editar Incidencia' : 'Nueva Incidencia'}
                </h5>
                {(() => {
                  const currentStatusInfo = getStatusBadge(
                    selectedTag && Number(selectedTag.iEstadoIncidencia) === 3
                      ? 3
                      : (formResponsible ? 2 : 1),
                    isDark
                  );
                  return (
                    <span
                      className="badge rounded-pill px-2.5 py-1 fw-semibold d-inline-flex align-items-center"
                      style={{
                        backgroundColor: currentStatusInfo.bg,
                        color: currentStatusInfo.color,
                        border: `1px solid ${currentStatusInfo.border}`,
                        fontSize: '0.74rem'
                      }}
                    >
                      <span
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: currentStatusInfo.color,
                          marginRight: '6px'
                        }}
                      />
                      <span>{currentStatusInfo.label}</span>
                    </span>
                  );
                })()}
              </div>
              <p
                className="small mb-0"
                style={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: '0.84rem'
                }}
              >
                Agrupa tickets para resolver un problema recurrente.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-sm p-1 rounded-circle border-0 text-muted hover-opacity shadow-none"
              onClick={onClose}
              disabled={isSaving}
              style={{ fontSize: '1.1rem', width: '32px', height: '32px' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Formulario */}
          <form onSubmit={onSave}>
            <div
              className="modal-body p-4 pt-3 d-flex flex-column gap-3"
              style={{ maxHeight: '74vh', overflowY: 'auto' }}
            >
              {/* Título de la incidencia */}
              <div>
                <label
                  className="form-label small fw-bold mb-1.5"
                  style={{ color: isDark ? '#cbd5e1' : '#1e293b', fontSize: '0.84rem' }}
                >
                  Título de la incidencia <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control shadow-none"
                  placeholder="Ej. Problemas de acceso"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  disabled={isSaving}
                  required
                  style={{
                    backgroundColor: isDark ? '#1a2636' : '#f4f7fc',
                    borderColor: isDark ? '#293a4f' : '#e2e8f0',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '0.88rem',
                    padding: '0.70rem 0.95rem',
                    borderRadius: '12px'
                  }}
                />
              </div>

              {/* Responsable de la Incidencia (Opcional) */}
              <div>
                <label
                  className="form-label small fw-bold mb-1.5 d-flex align-items-center justify-content-between"
                  style={{ color: isDark ? '#cbd5e1' : '#1e293b', fontSize: '0.84rem' }}
                >
                  <span className="d-flex align-items-center gap-1.5">
                    <FontAwesomeIcon icon={faUserTie} style={{ color: '#6366f1', fontSize: '0.8rem', marginRight: '4px' }} />
                    <span>Asignar Responsable (usuarios del sistema)</span>
                  </span>
                  <span className="text-muted fw-normal" style={{ fontSize: '0.72rem' }}>
                    Opcional
                  </span>
                </label>
                <select
                  className="form-select shadow-none"
                  value={formResponsible || ''}
                  onChange={e => setFormResponsible(e.target.value ? Number(e.target.value) : null)}
                  disabled={isSaving || isLoadingAgents}
                  style={{
                    backgroundColor: isDark ? '#1a2636' : '#f4f7fc',
                    borderColor: isDark ? '#293a4f' : '#e2e8f0',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    fontSize: '0.86rem',
                    padding: '0.65rem 0.95rem',
                    borderRadius: '12px'
                  }}
                >
                  <option value="">
                    {isLoadingAgents ? 'Cargando usuarios del sistema...' : 'Sin responsable asignado (permanece Abierta)'}
                  </option>
                  {agentsList.map(agent => (
                    <option key={agent.idUsuario} value={agent.idUsuario}>
                      {agent.vNombreCompleto || `${agent.vNombre || ''} ${agent.vApellidos || ''}`.trim() || `Usuario #${agent.idUsuario}`}
                    </option>
                  ))}
                </select>
                <div className="small mt-1 text-muted" style={{ fontSize: '0.73rem' }}>
                  {formResponsible
                    ? 'Al asignar un responsable, el estatus de la incidencia cambia a "En Progreso".'
                    : 'Sin responsable asignado, la incidencia se crea como "Abierta".'}
                </div>
              </div>

              {/* Descripción del problema con Toolbar Markdown */}
              <div>
                <label
                  className="form-label small fw-bold mb-1.5"
                  style={{ color: isDark ? '#cbd5e1' : '#1e293b', fontSize: '0.84rem' }}
                >
                  Descripción del problema <span className="text-danger">*</span>
                </label>

                <div
                  className="border overflow-hidden"
                  style={{
                    borderColor: isDark ? '#293a4f' : '#e2e8f0',
                    backgroundColor: isDark ? '#1a2636' : '#f4f7fc',
                    borderRadius: '14px'
                  }}
                >
                  {/* Toolbar Markdown */}
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-1.5 border-bottom flex-wrap gap-1"
                    style={{
                      borderColor: isDark ? '#293a4f' : '#e2e8f0',
                      backgroundColor: isDark ? '#162232' : '#f8fafd'
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Negrita (**texto**)"
                        onClick={() => handleInsertMarkdown('**', '**', 'texto en negrita')}
                      >
                        <FontAwesomeIcon icon={faBold} style={{ fontSize: '0.80rem' }} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Cursiva (*texto*)"
                        onClick={() => handleInsertMarkdown('*', '*', 'texto en cursiva')}
                      >
                        <FontAwesomeIcon icon={faItalic} style={{ fontSize: '0.80rem' }} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Enlace ([texto](url))"
                        onClick={() => handleInsertMarkdown('[', '](https://...)', 'enlace')}
                      >
                        <FontAwesomeIcon icon={faLink} style={{ fontSize: '0.80rem' }} />
                      </button>
                      <span className="text-muted opacity-40 mx-1">|</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Lista con viñetas"
                        onClick={() => handleInsertMarkdown('\n• ', '', 'Elemento')}
                      >
                        <FontAwesomeIcon icon={faListUl} style={{ fontSize: '0.80rem' }} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Lista numerada"
                        onClick={() => handleInsertMarkdown('\n1. ', '', 'Elemento')}
                      >
                        <FontAwesomeIcon icon={faListOl} style={{ fontSize: '0.80rem' }} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-1 text-muted text-decoration-none hover-opacity"
                        title="Código"
                        onClick={() => handleInsertMarkdown('`', '`', 'código')}
                      >
                        <FontAwesomeIcon icon={faCode} style={{ fontSize: '0.80rem' }} />
                      </button>
                    </div>


                  </div>

                  {/* Textarea con soporte para pegar imágenes con Ctrl+V */}
                  <textarea
                    ref={textareaRef}
                    className="form-control border-0 shadow-none"
                    rows={5}
                    placeholder="Describe el fallo técnico, impacto, componentes afectados o pasos para reproducir..."
                    value={formDescription}
                    onChange={e => setFormDescription(e.target.value)}
                    disabled={isSaving}
                    required
                    style={{
                      backgroundColor: 'transparent',
                      color: isDark ? '#f8fafc' : '#0f172a',
                      fontSize: '0.86rem',
                      lineHeight: '1.55',
                      padding: '0.85rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Dropzone de Carga de Archivos */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.log,.json"
                  className="d-none"
                  onChange={e => processFiles(e.target.files)}
                />

                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border border-2 border-dashed text-center cursor-pointer transition-all"
                  style={{
                    backgroundColor: isDragging
                      ? isDark
                        ? 'rgba(99, 102, 241, 0.15)'
                        : '#eef2ff'
                      : isDark
                        ? 'rgba(15, 23, 42, 0.4)'
                        : '#f8fafd',
                    borderColor: isDragging
                      ? '#6366f1'
                      : isDark
                        ? '#2a3b50'
                        : '#e0e7ff',
                    borderRadius: '16px',
                    padding: '1.75rem 1rem',
                    cursor: 'pointer'
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle"
                    style={{
                      width: '46px',
                      height: '46px',
                      backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
                      color: '#6366f1'
                    }}
                  >
                    <FontAwesomeIcon icon={faCloudUploadAlt} style={{ fontSize: '1.30rem' }} />
                  </div>
                  <div
                    className="fw-semibold mb-1"
                    style={{ color: isDark ? '#f8fafc' : '#1e293b', fontSize: '0.86rem' }}
                  >
                    <span style={{ color: '#4f46e5', fontWeight: 600 }}>Haz clic para examinar</span>{' '}
                    <span className="fw-normal">o arrastra tus archivos aquí</span>
                  </div>
                  <div className="small text-muted" style={{ fontSize: '0.74rem' }}>
                    PNG, JPG, logs o PDF hasta 20 MB cada uno <span className="opacity-75">• O pega capturas con Ctrl + V</span>
                  </div>
                </div>

                {/* Lista de Archivos Preparados */}
                {filesList.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span
                        className="small fw-bold text-uppercase"
                        style={{
                          fontSize: '0.70rem',
                          letterSpacing: '0.6px',
                          color: isDark ? '#94a3b8' : '#64748b'
                        }}
                      >
                        Archivos Preparados ({filesList.length})
                      </span>
                      <span className="small text-muted" style={{ fontSize: '0.74rem' }}>
                        {formatFileSize(totalBytes)} total
                      </span>
                    </div>

                    <div className="d-flex flex-column gap-2">
                      {filesList.map(item => (
                        <div
                          key={item.id}
                          className="p-2 px-3 border d-flex align-items-center justify-content-between"
                          style={{
                            backgroundColor: isDark ? '#1a2636' : '#f4f7fc',
                            borderColor: isDark ? '#293a4f' : '#e2e8f0',
                            borderRadius: '12px'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden" style={{ gap: '10px' }}>
                            <div
                              className="d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: '24px',
                                fontSize: '1.05rem'
                              }}
                            >
                              {renderFileIcon(item.fileName)}
                            </div>
                            <span
                              className="fw-semibold text-truncate"
                              style={{
                                fontSize: '0.84rem',
                                color: isDark ? '#f8fafc' : '#0f172a',
                                maxWidth: '340px'
                              }}
                              title={item.fileName}
                            >
                              {item.fileName}
                            </span>
                            <span
                              className="small text-muted flex-shrink-0"
                              style={{ fontSize: '0.73rem', marginLeft: '6px' }}
                            >
                              {formatFileSize(item.fileSize)}
                            </span>
                          </div>

                          <div className="d-flex align-items-center gap-3 flex-shrink-0" style={{ gap: '12px' }}>
                            <span
                              style={{
                                color: '#6366f1',
                                fontSize: '0.95rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Listo para subir"
                            >
                              <FontAwesomeIcon icon={faCheckCircle} />
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm p-1 text-muted border-0 bg-transparent hover-opacity d-inline-flex align-items-center justify-content-center"
                              style={{
                                width: '24px',
                                height: '24px',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleRemoveFile(item.id)}
                              title="Quitar archivo"
                            >
                              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.88rem' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer con botones Cancelar y Crear Incidencia */}
            <div
              className="modal-footer border-top px-4 py-3 d-flex align-items-center justify-content-end gap-2"
              style={{ borderColor: isDark ? '#223247' : '#f1f5f9' }}
            >
              <button
                type="button"
                className="btn btn-sm btn-link text-muted text-decoration-none fw-semibold px-3 shadow-none hover-opacity"
                onClick={onClose}
                disabled={isSaving}
                style={{ fontSize: '0.85rem' }}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="btn btn-sm rounded-pill px-4 fw-semibold d-flex align-items-center gap-2 shadow-none text-white"
                disabled={isSaving || !formName.trim() || !formDescription.trim()}
                style={{
                  backgroundColor: '#4f46e5',
                  borderColor: '#4f46e5',
                  fontSize: '0.85rem',
                  paddingTop: '0.55rem',
                  paddingBottom: '0.55rem'
                }}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>{selectedTag ? 'Guardar Cambios' : 'Crear Incidencia'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TagCreateModal;

